import { useEffect, useRef, useState } from 'react';

interface TiltLogoProps {
  src: string;
  alt?: string;
  className?: string;
  /** Max rotation in degrees */
  maxTilt?: number;
}

/**
 * Logo with 3D tilt that follows the cursor on desktop and the device
 * orientation (gyroscope) on mobile/tablet.
 */
export function TiltLogo({ src, alt = '', className, maxTilt = 18 }: TiltLogoProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState<string>('');
  const orientationActive = useRef(false);

  const applyTilt = (rotateX: number, rotateY: number, scale = 1.06) => {
    setTransform(
      `perspective(800px) rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg) scale(${scale})`
    );
  };

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (orientationActive.current) return; // prefer device orientation when active
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    applyTilt(-(y - 0.5) * 2 * maxTilt, (x - 0.5) * 2 * maxTilt);
  };

  const handleLeave = () => {
    if (orientationActive.current) return;
    applyTilt(0, 0, 1);
  };

  // Device orientation (mobile)
  useEffect(() => {
    const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
    if (!isTouch) return;

    const handleOrientation = (event: DeviceOrientationEvent) => {
      const beta = event.beta ?? 0;   // front-back tilt: -180..180
      const gamma = event.gamma ?? 0; // left-right tilt: -90..90
      // Use a small base range for natural movement
      const rotateX = Math.max(-maxTilt, Math.min(maxTilt, ((beta - 45) / 45) * maxTilt));
      const rotateY = Math.max(-maxTilt, Math.min(maxTilt, (gamma / 45) * maxTilt));
      orientationActive.current = true;
      applyTilt(-rotateX, rotateY);
    };

    const DOE = (window as any).DeviceOrientationEvent;
    if (DOE && typeof DOE.requestPermission === 'function') {
      // iOS requires explicit permission — skip gyroscope on iOS
      return;
    }
    window.addEventListener('deviceorientation', handleOrientation);
    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, [maxTilt]);

  return (
    <div className="inline-flex flex-col items-center gap-2">
      <div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        className="inline-block [transform-style:preserve-3d] will-change-transform"
        style={{
          transform,
          transition: 'transform 200ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <img src={src} alt={alt} className={className} draggable={false} />
      </div>
    </div>
  );
}
